import { useQuery } from "@apollo/client";
import {
  AnchorButton,
  Button,
  Card,
  Classes,
  Dialog,
  Icon,
} from "@blueprintjs/core";
import _ from "lodash";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CustomerOrder,
  CustomerOrder_State,
  CustomerOrder_Type,
} from "../../../../api/schema";
import { FlexContainer, FlexGrow } from "../../../../components/flex/Flex";
import { ItemRenderers } from "../../../../components/ItemRenderer";
import { ItemSelect } from "../../../../components/select/ItemSelect";
import { Table } from "../../../../components/Table";
import { print_url } from "../../../../config";
import { useGlobalState } from "../../../../Context";
import { UserLanguage } from "../../../../lang/lang";
import {
  CustomerOrderListViewQuery,
  ICustomerOrderListViewQuery,
} from "../graphql";
import { IconMapperStateIntent, IconMapperTypeIntent } from "../helper";
import { CustomerOrderViewController } from "../View/OrderView";
import { CustomerOrderListViewTableColumns } from "./table";

export const AllCustomerOrderListView = (props: {}) => {
  const { global_state } = useGlobalState();

  const [customerListViewData, updateCustomerListViewData] =
    useState<ICustomerOrderListViewQuery | null>(null);

  const onData = (data: ICustomerOrderListViewQuery) =>
    updateCustomerListViewData(data);

  const listQuery = useQuery<ICustomerOrderListViewQuery>(
    CustomerOrderListViewQuery,
    {
      fetchPolicy: "no-cache",
      onCompleted: onData,
    }
  );

  const default_states = [
    CustomerOrder_State.accepted,
    CustomerOrder_State.processing,
    CustomerOrder_State.ready_for_pickup,
    CustomerOrder_State.wait_for_parts,
    CustomerOrder_State.wait_for_reply,
  ];

  const default_types = [
    CustomerOrder_Type.part_order,
    CustomerOrder_Type.inquiry,
    CustomerOrder_Type.repair,
  ];

  const [filterType, setFilterType] =
    useState<CustomerOrder_Type[]>(default_types);

  const [filterState, setFilterState] =
    useState<CustomerOrder_State[]>(default_states);

  const [openOrderId, setOpenOrderId] = useState<number | null>(null);
  const [openOrderHasChanges, setOpenOrderHasChanges] =
    useState<boolean>(false);

  const on_open_order_change = (b: boolean) => setOpenOrderHasChanges(b);

  const filteredItems = () => {
    if (!customerListViewData) return [];
    return _.filter(customerListViewData.customerOrders, (order) => {
      if (_.indexOf(filterState, order.state) === -1) return false;
      if (_.indexOf(filterType, order.type) === -1) return false;
      return true;
    });
  };

  return (
    <FlexContainer>
      <Card elevation={3}>
        <div
          style={{
            display: "flex",
          }}
        >
          <div>
            <h2>{UserLanguage().pages.customerOrder.list}</h2>
          </div>
          <div style={{ marginLeft: 10 }} />
          <ItemSelect<CustomerOrder_State>
            itemRenderer={ItemRenderers.CustomerOrder_State}
            items={_.values(CustomerOrder_State)}
            onSelectionChange={async (os) => setFilterState(os)}
            defaultItems={default_states}
            translate={(os) => UserLanguage().data.customerOrder.states[os]}
            intent={(os) => IconMapperStateIntent[os]}
          />
          <div style={{ marginLeft: 10 }} />
          <ItemSelect<CustomerOrder_Type>
            itemRenderer={ItemRenderers.CustomerOrder_Type}
            items={_.values(CustomerOrder_Type)}
            onSelectionChange={async (ot) => setFilterType(ot)}
            defaultItems={default_types}
            translate={(ot) => UserLanguage().data.customerOrder.types[ot]}
            intent={(ot) => IconMapperTypeIntent[ot]}
          />
          <div style={{ marginLeft: 10 }} />
          <FlexGrow />
          <div>
            <Link to="/customer/order/new">
              <Button intent="success" icon="plus">
                {UserLanguage().actions.create}
              </Button>
            </Link>
          </div>
        </div>
        <Table<CustomerOrder>
          columns={CustomerOrderListViewTableColumns({ setOpenOrderId })}
          loading={listQuery.loading}
          items={filteredItems()}
        />
        <Dialog
          className={global_state.dark_mode ? Classes.DARK : undefined}
          isOpen={openOrderId !== null}
          style={{ width: "95%" }}
        >
          <div className="bp4-dialog-header">
            <Icon icon="document-open" />
            <h5 className="bp4-heading">
              {UserLanguage().pages.customerOrder.view} /{" "}
              <span style={{ color: "#1177FF" }}>{openOrderId}</span>
            </h5>
            <AnchorButton
              target="_blank"
              icon="print"
              minimal
              href={print_url + "customer/order/" + openOrderId}
            />
            <Button
              minimal
              icon="cross"
              disabled={openOrderHasChanges}
              onClick={() => setOpenOrderId(null)}
            />
          </div>
          <CustomerOrderViewController
            id={openOrderId}
            changes={on_open_order_change}
            showHeader={false}
          />
        </Dialog>
      </Card>
    </FlexContainer>
  );
};
